import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExpenses } from '../../contexts/ExpenseContext';
import { useGoals } from '../../contexts/GoalContext';
import { useChecklists } from '../../contexts/ChecklistContext';
import { useAuth } from '../../hooks/useAuth';
import { useCurrency } from '../../hooks/useCurrency';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatDate, getTodayString } from '../../utils/dateUtils';
import { BarChart, Bar, Cell, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { summariseMoney } from '../../utils/moneySummary';
import { buildCategoryScale, foldToTop, CHART_GRID, CHART_OTHER, CHART_SLOTS } from '../../utils/chartPalette';
import UpgradeBanner from '../common/UpgradeBanner';

const emptyWalletForm = { name: '', amount: '' };

// Axis ticks, not values: a full VND figure on every gridline is unreadable, and the
// exact number is one hover away.
function compactAmount(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(value);
}

function monthPrefix(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const PULSE_RING_RADIUS = 20;
const PULSE_RING_CIRCUMFERENCE = 2 * Math.PI * PULSE_RING_RADIUS;

// Small progress ring for the pulse row. Two stacked circles rather than a bar because
// these sit beside a number, and a ring reads as "a proportion of a whole" at a glance
// where a 4px bar at this size just reads as a line.
function PulseRing({ percent, tone }) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <svg className="dash-pulse-ring" width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r={PULSE_RING_RADIUS} fill="none" stroke="var(--border-color)" strokeWidth="4" />
      <circle
        cx="24"
        cy="24"
        r={PULSE_RING_RADIUS}
        fill="none"
        stroke={tone}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={PULSE_RING_CIRCUMFERENCE}
        strokeDashoffset={PULSE_RING_CIRCUMFERENCE * (1 - clamped / 100)}
        transform="rotate(-90 24 24)"
      />
    </svg>
  );
}

// Recharts hands tooltips the hovered row; these render it in the app's own tooltip
// shell so charts don't introduce a second visual language for the same job.
function DailySpendTooltip({ active, payload, label, formatCurrency, t }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{t('charts.dayLabel', { day: label })}</strong>
      <span>{entry.isFuture ? t('charts.notYet') : formatCurrency(entry.amount)}</span>
    </div>
  );
}

function CategoryTooltip({ active, payload, formatCurrency }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{entry.name}</strong>
      <span>{formatCurrency(entry.value)}</span>
    </div>
  );
}

// Value sits just past the end of its bar. Labelling every bar is fine here because
// there are at most seven, and the alternative is a numeric axis read sideways.
function CategoryValueLabel({ x, y, width, height, value, formatCurrency }) {
  if (!width || width <= 0) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      dominantBaseline="middle"
      className="dash-chart-value-label"
    >
      {formatCurrency(value)}
    </text>
  );
}

function greetingKey(hour) {
  if (hour < 12) return 'greeting.morning';
  if (hour < 17) return 'greeting.afternoon';
  return 'greeting.evening';
}

function MainDashboard() {
  const { expenses, budgets, wallets, addWallet, updateWallet, deleteWallet } = useExpenses();
  const { goals } = useGoals();
  const { checklists, toggleTask } = useChecklists();
  const { username, isPro } = useAuth();
  const { formatCurrency, currency, toVnd, fromVnd } = useCurrency();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { limits, openUpgradeModal } = useSubscription();
  const { t, i18n } = useTranslation('dashboard');
  const { t: tSub } = useTranslation('subscription');
  const { t: tExpenses } = useTranslation('expenses');
  const walletsAtLimit = !isPro && wallets.length >= limits.wallets;
  const otherCategoryLabel = tExpenses('form.otherCategory');

  const today = new Date();
  const todayIso = getTodayString();
  const currentMonthPrefix = monthPrefix(today);
  const lastMonthPrefix = monthPrefix(new Date(today.getFullYear(), today.getMonth() - 1, 1));

  const monthlyExpenses = useMemo(() => expenses.filter((item) => item.date.startsWith(currentMonthPrefix) && (item.kind || 'expense') !== 'income'), [expenses, currentMonthPrefix]);
  const monthlyTotal = useMemo(() => monthlyExpenses.reduce((sum, item) => sum + item.amount, 0), [monthlyExpenses]);
  const lastMonthTotal = useMemo(() => expenses.filter((item) => item.date.startsWith(lastMonthPrefix) && (item.kind || 'expense') !== 'income').reduce((sum, item) => sum + item.amount, 0), [expenses, lastMonthPrefix]);
  const spendDelta = lastMonthTotal > 0 ? Math.round(((monthlyTotal - lastMonthTotal) / lastMonthTotal) * 100) : null;

  const monthBudgets = useMemo(() => budgets.filter((budget) => budget.recurring !== false || budget.month === currentMonthPrefix), [budgets, currentMonthPrefix]);
  // The Dashboard has no period picker, so it reports the current month - the default
  // the Expenses page also opens on. Same helper, so the two always agree.
  const summary = useMemo(() => summariseMoney({
    expenses,
    budgets: monthBudgets,
    start: `${currentMonthPrefix}-01`,
    end: `${currentMonthPrefix}-31`,
  }), [expenses, monthBudgets, currentMonthPrefix]);
  const monthlyIncome = summary.income;
  const monthBudgetCategories = useMemo(() => {
    const seen = new Map();
    monthBudgets.forEach((item) => {
      if (!seen.has(item.category)) seen.set(item.category, item);
    });
    return Array.from(seen.values());
  }, [monthBudgets]);
  // One scale for the whole page, keyed on the budget category list rather than on
  // whatever happens to be in view, so a category keeps its colour between the dot in
  // the expense list and its bar in the chart below.
  const colorForCategory = useMemo(
    () => buildCategoryScale(monthBudgetCategories.map((item) => item.category)),
    [monthBudgetCategories],
  );
  const categoryColor = useCallback((category) => {
    if (category === otherCategoryLabel) return 'color-mix(in srgb, var(--text-color) 45%, transparent)';
    return colorForCategory(category);
  }, [colorForCategory, otherCategoryLabel]);

  const topExpenses = useMemo(() => [...monthlyExpenses].sort((a, b) => b.amount - a.amount).slice(0, 5), [monthlyExpenses]);

  // Spending per calendar day of the current month. Days with nothing spent stay in the
  // series as zero-height bars: the gaps are the story (which days you didn't spend),
  // and dropping them would compress the month into a misleading run of activity.
  const dailySpend = useMemo(() => {
    const [year, month] = currentMonthPrefix.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const currentDay = Number(todayIso.slice(8, 10));
    const totals = new Array(daysInMonth).fill(0);
    monthlyExpenses.forEach((item) => {
      const day = Number(item.date.slice(8, 10));
      if (day >= 1 && day <= daysInMonth) totals[day - 1] += item.amount;
    });
    return totals.map((amount, index) => ({
      day: index + 1,
      amount,
      isFuture: index + 1 > currentDay,
    }));
  }, [monthlyExpenses, currentMonthPrefix, todayIso]);
  const hasDailySpend = dailySpend.some((entry) => entry.amount > 0);

  // Where the money actually went, biggest first. Anything past the sixth category is
  // folded into one "Other" bar rather than being given a recycled colour.
  const spendByCategory = useMemo(() => {
    const totals = new Map();
    monthlyExpenses.forEach((item) => {
      totals.set(item.category, (totals.get(item.category) || 0) + item.amount);
    });
    const rows = Array.from(totals, ([name, value]) => ({ name, value }));
    return foldToTop(rows, { otherLabel: otherCategoryLabel });
  }, [monthlyExpenses, otherCategoryLabel]);
  const completedGoals = goals.filter((goal) => goal.status === 'Completed').length;
  const completionRate = goals.length ? Math.round((completedGoals / goals.length) * 100) : 0;
  const nearingGoal = useMemo(() => [...goals].filter((goal) => goal.status !== 'Completed' && goal.status !== 'Cancelled').sort((a, b) => new Date(a.endDate) - new Date(b.endDate)).slice(0, 3), [goals]);

  const todayChecklist = checklists.find((item) => item.date === getTodayString());
  const todayTaskCount = todayChecklist?.tasks.length || 0;
  const todayDoneCount = todayChecklist?.tasks.filter((task) => task.completed).length || 0;
  const todayCompletion = todayTaskCount ? Math.round((todayDoneCount / todayTaskCount) * 100) : 0;

  const totalWalletAmount = useMemo(() => wallets.reduce((sum, wallet) => sum + wallet.amount, 0), [wallets]);
  // An overdue goal is the one thing on this page that needs acting on rather than
  // reading, so it gets called out instead of being left for the user to spot.
  const overdueCount = useMemo(
    () => goals.filter((goal) => goal.status !== 'Completed' && goal.status !== 'Cancelled' && goal.endDate < getTodayString()).length,
    [goals],
  );
  const monthLabel = today.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });

  const [walletForm, setWalletForm] = useState(emptyWalletForm);
  const [editingWalletId, setEditingWalletId] = useState(null);
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [walletErrors, setWalletErrors] = useState({});

  const handleToggleTask = (taskId) => {
    if (!todayChecklist) return;
    toggleTask(todayChecklist.id, taskId);
  };

  const openAddWallet = () => {
    if (walletsAtLimit) {
      openUpgradeModal('wallet');
      return;
    }
    setEditingWalletId(null);
    setWalletForm(emptyWalletForm);
    setWalletErrors({});
    setShowWalletForm(true);
  };

  const openEditWallet = (wallet) => {
    setEditingWalletId(wallet.id);
    setWalletForm({ name: wallet.name, amount: String(fromVnd(wallet.amount)) });
    setWalletErrors({});
    setShowWalletForm(true);
  };

  const closeWalletForm = useCallback(() => {
    setShowWalletForm(false);
    setEditingWalletId(null);
    setWalletForm(emptyWalletForm);
    setWalletErrors({});
  }, []);

  const handleWalletSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = walletForm.name.trim();
    const validation = {};
    if (!trimmedName) validation.name = t('walletForm.nameRequired');
    if (walletForm.amount === '' || Number.isNaN(Number(walletForm.amount)) || Number(walletForm.amount) < 0) {
      validation.amount = t('walletForm.amountValid');
    }
    if (Object.keys(validation).length) {
      setWalletErrors(validation);
      return;
    }

    const payload = { name: trimmedName, amount: toVnd(walletForm.amount) };
    try {
      if (editingWalletId) {
        await updateWallet({ id: editingWalletId, ...payload });
        addToast(t('toast.walletUpdated'), 'success');
      } else {
        await addWallet(payload);
        addToast(t('toast.walletAdded'), 'success');
      }
      closeWalletForm();
    } catch (error) {
      addToast(error.message || t('toast.somethingWrong'), 'error');
    }
  };

  const handleDeleteWallet = async (wallet) => {
    const confirmed = await confirm(t('confirmDeleteWallet', { name: wallet.name }));
    if (!confirmed) return;
    await deleteWallet(wallet.id);
    addToast(t('toast.walletDeleted'), 'success');
  };

  useEffect(() => {
    if (!showWalletForm) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeWalletForm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showWalletForm, closeWalletForm]);

  return (
    <section className="page-card">
      <div className="page-header dashboard-header">
        <div>
          <p className="eyebrow">{formatDate(getTodayString())}</p>
          <h1 className="display-title">{t(greetingKey(today.getHours()), { name: username })}</h1>
          <p className="dashboard-header-sub">{t('subtitle')}</p>
        </div>
        <div className="page-actions">
          <Link to="/checklist" className="text-btn">{t('quickActions.task')}</Link>
          <Link to="/goals" className="text-btn">{t('quickActions.goal')}</Link>
          <Link to="/expenses" className="primary-btn">{t('quickActions.transaction')}</Link>
        </div>
      </div>

      <UpgradeBanner />

      {/* The month's headline and its shape belong side by side: the band says how
          much, the chart says when. Stacking them as two full-width strips wasted the
          width of the page and pushed everything else below the fold. */}
      <div className="dash-lead is-triple">
        <section className="dash-money" aria-label={t('thisMonth')}>
          <header className="dash-money-head">
            <span className="eyebrow">{t('thisMonth')}</span>
            <span className="dash-money-month">{monthLabel}</span>
          </header>
          <div className="dash-money-figures">
            <div className="dash-figure tone-income">
              <span className="dash-figure-label">{t('income')}</span>
              <strong className="dash-figure-value">{formatCurrency(monthlyIncome)}</strong>
              <span className="dash-figure-note">&nbsp;</span>
            </div>
            <div className="dash-figure tone-spending">
              <span className="dash-figure-label">{t('spending')}</span>
              <strong className="dash-figure-value">{formatCurrency(monthlyTotal)}</strong>
              {spendDelta === null ? (
                <span className="dash-figure-note">{t('noLastMonthData')}</span>
              ) : (
                <span className={`dash-figure-note dash-delta${spendDelta > 0 ? ' up' : spendDelta < 0 ? ' down' : ''}`}>
                  {spendDelta > 0 ? '▲' : spendDelta < 0 ? '▼' : '—'} {Math.abs(spendDelta)}% {t('vsLastMonth')}
                </span>
              )}
            </div>
            <div className="dash-figure tone-saving">
              <span className="dash-figure-label">{t('saving')}</span>
              <strong className="dash-figure-value">{formatCurrency(summary.saving)}</strong>
              <span className="dash-figure-note">
                {t('savingBreakdown', {
                  setAside: formatCurrency(summary.setAside),
                  unspent: formatCurrency(summary.unspent),
                })}
              </span>
            </div>
          </div>
        </section>

          <section className="dash-chart-card">
            <header className="dash-chart-head">
              <h2>{t('charts.dailySpend')}</h2>
              <span className="dash-chart-sub">{monthLabel}</span>
            </header>
            {hasDailySpend ? (
              <div className="dash-chart-body">
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={dailySpend} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tickLine={false}
                      axisLine={{ stroke: CHART_GRID }}
                      tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                      // A tick per day is unreadable at this width; every fifth keeps the
                      // month legible while the bars still sit on their real dates.
                      interval={4}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={46}
                      tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                      tickFormatter={compactAmount}
                    />
                    <Tooltip cursor={{ fill: 'currentColor', opacity: 0.06 }} content={<DailySpendTooltip formatCurrency={formatCurrency} t={t} />} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]} maxBarSize={18}>
                      {dailySpend.map((entry) => (
                        // Days that haven't happened yet are drawn flat and grey rather
                        // than omitted, so the month's remaining runway stays visible.
                        <Cell key={entry.day} fill={entry.isFuture ? CHART_GRID : CHART_SLOTS[0]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="dash-chart-empty">{t('charts.noSpendYet')}</p>
            )}
          </section>

          <section className="dash-chart-card">
            <header className="dash-chart-head">
              <h2>{t('charts.byCategory')}</h2>
              <span className="dash-chart-sub">{monthLabel}</span>
            </header>
            {spendByCategory.length > 0 ? (
              <div className="dash-chart-body">
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={spendByCategory} layout="vertical" margin={{ top: 4, right: 52, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={96}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12, fill: 'currentColor', opacity: 0.75 }}
                    />
                    <Tooltip cursor={{ fill: 'currentColor', opacity: 0.06 }} content={<CategoryTooltip formatCurrency={formatCurrency} />} />
                    {/* Direct labels on a handful of bars, rather than an axis of numbers
                        nobody reads across. */}
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22} label={<CategoryValueLabel formatCurrency={formatCurrency} />}>
                      {spendByCategory.map((entry, index) => (
                        <Cell key={entry.name} fill={entry.isOther ? CHART_OTHER : (CHART_SLOTS[index] || CHART_OTHER)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="dash-chart-empty">{t('charts.noSpendYet')}</p>
            )}
          </section>
      </div>

      {/* Progress, not money - deliberately quieter than the band above so the page has
          a first thing to look at instead of six equal-weight tiles. */}
      <div className="dash-pulse">
        <Link to="/goals" className="dash-pulse-card">
          <PulseRing percent={completionRate} tone="var(--orchid-color)" />
          <div className="dash-pulse-body">
            <span className="dash-pulse-label">{t('goalProgress')}</span>
            <strong className="dash-pulse-value">{completionRate}%</strong>
            <span className="dash-pulse-caption">
              {t('goalsComplete', { completed: completedGoals, total: goals.length })}
            </span>
            {overdueCount > 0 && (
              <span className="dash-pulse-flag">⚠ {t('overdueGoals', { count: overdueCount })}</span>
            )}
          </div>
        </Link>

        <Link to="/checklist" className="dash-pulse-card">
          <PulseRing percent={todayCompletion} tone="var(--secondary-color)" />
          <div className="dash-pulse-body">
            <span className="dash-pulse-label">{t('checklistToday')}</span>
            <strong className="dash-pulse-value">
              {todayChecklist ? `${todayDoneCount}/${todayTaskCount}` : '—'}
            </strong>
            <span className="dash-pulse-caption">
              {!todayChecklist
                ? t('noChecklistToday')
                : todayTaskCount > 0 && todayDoneCount === todayTaskCount
                  ? t('allTasksDone')
                  : todayChecklist.title}
            </span>
          </div>
        </Link>

        {/* No ring - a wallet balance has no denominator to be a percentage of. The
            glyph tile keeps the row's rhythm without inventing a fake progress value. */}
        <div className="dash-pulse-card is-static">
          <span className="dash-pulse-glyph" aria-hidden="true">👛</span>
          <div className="dash-pulse-body">
            <span className="dash-pulse-label">{t('totalWallet')}</span>
            <strong className="dash-pulse-value">{formatCurrency(totalWalletAmount)}</strong>
            <span className="dash-pulse-caption">
              {t(wallets.length === 1 ? 'walletCountOne' : 'walletCountOther', { count: wallets.length })}
            </span>
          </div>
        </div>
      </div>

      <div className="dashboard-content-grid">
        <div className="sheet-table-card dashboard-list-card is-primary">
          <div className="sheet-panel-header tint-pink">
            <span>{t('todayChecklist')}</span>
            {todayChecklist ? (
              <span className="dashboard-head-badge">{todayDoneCount}/{todayTaskCount}</span>
            ) : (
              <Link to="/checklist" className="text-btn">{t('common:actions.seeAll')}</Link>
            )}
          </div>
          {/* This panel runs the full width of the grid, and a single column of short
              task labels left most of that width empty. The tasks flow into as many
              columns as fit instead - a full-width list of one-line items is a column
              layout whether or not it admits it. */}
          <ul className={`dashboard-list${todayChecklist ? ' dashboard-task-grid' : ''}`}>
            {!todayChecklist && (
              <li className="dashboard-list-empty">
                <span className="dashboard-list-empty-msg">{t('noChecklistForToday')}</span>
                <Link to="/checklist" className="text-btn">{t('addChecklistCta')}</Link>
              </li>
            )}
            {todayChecklist?.tasks.map((task) => (
              <li key={task.id} className={`dashboard-task-row${task.completed ? ' done' : ''}`}>
                <label>
                  <input type="checkbox" checked={task.completed} onChange={() => handleToggleTask(task.id)} />
                  <span className={`dashboard-list-name${task.completed ? ' done' : ''}`}>{task.text}</span>
                </label>
              </li>
            ))}
          </ul>
          {todayChecklist && (
            <div className="dashboard-list-card-foot">
              <Link to="/checklist" className="text-btn">{t('common:actions.seeAll')}</Link>
            </div>
          )}
        </div>

        <div className="sheet-table-card dashboard-list-card">
          <div className="sheet-panel-header tint-orchid">
            <span>{t('goalsNearingCompletion')}</span>
            <Link to="/goals" className="text-btn">{t('common:actions.seeAll')}</Link>
          </div>
          <ul className="dashboard-list">
            {nearingGoal.length === 0 && (
              <li className="dashboard-list-empty">
                <span className="dashboard-list-empty-msg">{t('noGoalsInProgress')}</span>
                <Link to="/goals" className="text-btn">{t('addGoalCta')}</Link>
              </li>
            )}
            {nearingGoal.map((goal) => {
              const pct = goal.targetAmount
                ? Math.min(100, Math.round((goal.achievedAmount / goal.targetAmount) * 100))
                : Math.min(100, Math.round(((new Date() - new Date(goal.startDate)) / (new Date(goal.endDate) - new Date(goal.startDate))) * 100));
              return (
                <li key={goal.id} className="dashboard-goal-row">
                  <span className="dashboard-goal-top">
                    <span className="dashboard-list-name">{goal.title}</span>
                    <span className="dashboard-goal-due">{formatDate(goal.endDate)}</span>
                  </span>
                  <span className="dashboard-goal-bar-row">
                    <span className="progress-bar"><div style={{ width: `${pct}%` }} /></span>
                    <span className="dashboard-goal-pct">{pct}%</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="sheet-table-card dashboard-list-card">
          <div className="sheet-panel-header tint-lavender">
            <span>{t('topExpenses')}</span>
            <Link to="/expenses" className="text-btn">{t('common:actions.seeAll')}</Link>
          </div>
          <ul className="dashboard-list">
            {topExpenses.length === 0 && (
              <li className="dashboard-list-empty">
                <span className="dashboard-list-empty-msg">{t('noExpenses')}</span>
                <Link to="/expenses" className="text-btn">{t('addExpenseCta')}</Link>
              </li>
            )}
            {topExpenses.map((expense) => (
              <li key={expense.id}>
                <span className="dashboard-exp-dot" style={{ background: categoryColor(expense.category) }} aria-hidden="true" />
                <span className="dashboard-exp-main">
                  <span className="dashboard-list-name">{expense.name}</span>
                  <span className="dashboard-exp-cat">{expense.category}</span>
                </span>
                <strong>{formatCurrency(expense.amount)}</strong>
              </li>
            ))}
          </ul>
        </div>

        <div className="sheet-table-card dashboard-list-card">
          <div className="sheet-panel-header tint-indigo">
            <span>{t('wallets')}</span>
            <span>
              {walletsAtLimit && <span className="limit-hint at-cap">{tSub('limitHint', { used: wallets.length, limit: limits.wallets })}</span>}
              <button type="button" className="text-btn" onClick={openAddWallet}>{t('addWalletCta')}</button>
            </span>
          </div>
          <ul className="dashboard-list">
            {wallets.length === 0 && (
              <li className="dashboard-list-empty">
                <span className="dashboard-list-empty-msg">{t('noWallets')}</span>
                <button type="button" className="text-btn" onClick={openAddWallet}>{t('addWalletCta')}</button>
              </li>
            )}
            {wallets.map((wallet) => (
              <li key={wallet.id}>
                <span className="dashboard-list-name">{wallet.name}</span>
                <span className="dashboard-wallet-value">
                  <strong>{formatCurrency(wallet.amount)}</strong>
                  <span className="dashboard-wallet-actions">
                    <button type="button" className="text-btn icon-btn" onClick={() => openEditWallet(wallet)} aria-label="Edit">✎</button>
                    <button type="button" className="text-btn danger icon-btn" onClick={() => handleDeleteWallet(wallet)} aria-label="Delete">✕</button>
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showWalletForm && (
        <div className="modal-overlay" onClick={closeWalletForm}>
          <form onSubmit={handleWalletSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingWalletId ? t('walletForm.editTitle') : t('walletForm.addTitle')}</h3>
            <label>
              {t('walletForm.name')}
              <input autoFocus value={walletForm.name} onChange={(event) => setWalletForm({ ...walletForm, name: event.target.value })} />
              {walletErrors.name && <span className="error-text">{walletErrors.name}</span>}
            </label>
            <label>
              {t('walletForm.amount')}
              <div className="amount-input-group">
                <input type="number" min="0" step="0.01" value={walletForm.amount} onChange={(event) => setWalletForm({ ...walletForm, amount: event.target.value })} />
                <span className="amount-input-currency" title={currency.name}>{currency.symbol} {currency.code}</span>
              </div>
              {walletErrors.amount && <span className="error-text">{walletErrors.amount}</span>}
            </label>
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeWalletForm}>{t('walletForm.cancel')}</button>
              <button className="primary-btn" type="submit">{editingWalletId ? t('walletForm.saveChanges') : t('walletForm.addWallet')}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default MainDashboard;
